import {
  IPlpCategoryRoute,
  IPlpProductTypeRoute,
  plpCategoryRoutes,
  plpProductTypeRoutes
} from '@constants/plpProductTypeRouteConstants';
import { PlpPageRouteEnum } from '@routes/enum/plpPageRoute.enum';
import { ProductApiConstants } from '@constants/productApiConstants';

const PRODUCT_TYPE_SHORT_KEY = ProductApiConstants.FILTER_KEY_MAP.SHORT_KEYS['product_type'];

const splitUrl = (url?: string) => {
  const [path = '', ...rest] = (url || '').split('?');
  return { segments: path.split('/').filter(Boolean), query: rest.join('?') };
};

// Returns the category for a path segment, e.g. carpet, hardsurface, hard-surface
export const getPlpCategoryRoute = (segment?: string): IPlpCategoryRoute | undefined => {
  const value = segment?.toLowerCase();
  if (!value) return;

  return plpCategoryRoutes.find(({ seoPath, cmsPath, aliases }) => [seoPath, cmsPath, ...aliases].includes(value));
};

// Returns the product type landing page for a url, e.g. /carpet/carpet-tile?c=Blue
export const getPlpProductTypeRouteFromUrl = (url?: string): IPlpProductTypeRoute | undefined => {
  const { segments } = splitUrl(url);
  if (segments.length !== 2) return;

  const category = getPlpCategoryRoute(segments[0]);
  const slug = segments[1].toLowerCase();

  return category
    ? plpProductTypeRoutes.find((route) => route.plpPageRoute === category.plpPageRoute && route.slug === slug)
    : undefined;
};

// Returns the landing page path for a product type, e.g. (carpet, 'Carpet Tile') => /carpet/carpet-tile
export const getPlpProductTypePath = (plpPageRoute?: PlpPageRouteEnum | string, productType?: string) => {
  const category = getPlpCategoryRoute(plpPageRoute);
  const route = plpProductTypeRoutes.find(
    (item) =>
      item.plpPageRoute === category?.plpPageRoute && item.productType.toLowerCase() === productType?.toLowerCase()
  );

  return category && route ? `/${category.seoPath}/${route.slug}` : undefined;
};

// Returns the category page path, e.g. carpet => /carpet
export const getPlpCategoryPath = (plpPageRoute?: PlpPageRouteEnum | string) => {
  const category = getPlpCategoryRoute(plpPageRoute);
  return category ? `/${category.cmsPath}` : undefined;
};

// Product type landing pages don't exist in the CMS, they render the CMS page of their category
export const getCmsPath = (url?: string) => {
  const productTypeRoute = getPlpProductTypeRouteFromUrl(url);
  if (!productTypeRoute) return url || '';

  const { query } = splitUrl(url);
  return `${getPlpCategoryPath(productTypeRoute.plpPageRoute)}${query ? `?${query}` : ''}`;
};

// Returns the landing page url for legacy product type urls, e.g. /carpet?pt=Carpet%20Tile => /carpet/carpet-tile
export const getPlpProductTypeRedirectUrl = (path?: string, query?: Record<string, unknown>) => {
  const { segments } = splitUrl(path);
  const category = getPlpCategoryRoute(segments[0]);
  if (!category || segments.length > 2) return;

  const productType = query?.[PRODUCT_TYPE_SHORT_KEY];
  const productTypeRoute = getPlpProductTypeRouteFromUrl(path);
  const targetPath =
    segments.length === 1
      ? typeof productType === 'string'
        ? getPlpProductTypePath(category.plpPageRoute, productType)
        : undefined
      : productTypeRoute && segments[0].toLowerCase() !== category.seoPath
        ? `/${category.seoPath}/${productTypeRoute.slug}`
        : undefined;

  if (!targetPath) return;

  const params = new URLSearchParams();
  Object.entries(query || {}).forEach(([key, value]) => {
    if (segments.length === 1 && key === PRODUCT_TYPE_SHORT_KEY) return;
    (Array.isArray(value) ? value : [value]).forEach((item) => params.append(key, String(item)));
  });
  const search = params.toString();

  return `${targetPath}${search ? `?${search}` : ''}`;
};

// Converts app relative product type urls into their landing page url, e.g. /carpet?pt=Carpet Tile => /carpet/carpet-tile
export const toPlpProductTypeUrl = (url?: string) => {
  if (!url?.startsWith('/')) return url;

  const [path, search = ''] = url.split('?');
  const query: Record<string, string | string[]> = {};
  new URLSearchParams(search).forEach((value, key) => {
    const existing = query[key];
    query[key] = existing === undefined ? value : [...(Array.isArray(existing) ? existing : [existing]), value];
  });

  return getPlpProductTypeRedirectUrl(path, query) ?? url;
};
